import moment from 'moment';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Button,
  TextInput,
  SelectInput,
  TextAreaInput,
  ButtonsWrapper,
  DatePickerInput,
  CheckboxSelect,
  NumberInput,
} from '@thinknet-gmbh/platform-common-ui';

import {
  ProcessTaskStatusesEnum,
  ProcessTaskAssignEnum,
  PLATFORM_PERMISSIONS,
  ProcessTaskTypesEnum,
} from '@thinknet-gmbh/platform-common';

import { useGetAllEmployeesForList } from 'src/hooks/employees/get-all-employees-for-list.hook';
import { useUserCanManageDocument } from 'src/hooks/user-can-manage-document.hook';
import { useGetAllPlatformCompanies } from 'src/hooks/companies/get-all-platform-companies.hook';
import { useGetCurrentCompany } from 'src/hooks/companies/get-current-company.hook';
import { useGetJobLocationsByDepartment } from 'src/hooks/jobs/get-job-locations-by-department.hook';
import { useGetCurrentMemberAccount } from 'src/hooks/accounts/get-current-member-account.hook';

import { resetTaskFormFields } from '../../task-form.helpers';
import { Checklist } from '../checklist';

import { ITaskEditFormItemsProps } from './task-edit-form.interfaces';

const TaskEditForm = ({
  initialValue,
  onSubmit,
  onCancel,
  accountId,
  isProcessTaskTemplate,
}: ITaskEditFormItemsProps) => {
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation(['processBuilder', 'common', 'employee']);

  const { canEdit, canEditALl } = useUserCanManageDocument({
    platformPermission: PLATFORM_PERMISSIONS.taskManagement,
    createdBy: accountId,
  });

  const [assignTo, setAssignTo] = useState<string>(
    initialValue?.assignTo || ProcessTaskAssignEnum.PERSONS
  );
  const [departmentId, setDepartmentId] = useState<string>('');
  const { companies } = useGetAllPlatformCompanies();
  const { company } = useGetCurrentCompany();
  const { jobs } = useGetJobLocationsByDepartment(
    departmentId || (initialValue && initialValue.departmentId)
  );

  const { member } = useGetCurrentMemberAccount();

  const responsiblePersonFilter: {
    companyId: string;
    accountActive: boolean;
    showOwnRecordOnly?: boolean;
  } = { companyId: company._id, accountActive: true };

  const isEditOwnPermission = canEdit && !canEditALl;

  const isTaskOwner = accountId === initialValue?.createdBy;

  const isProcessesTask = initialValue.type === ProcessTaskTypesEnum.PROCESS;

  const { employees } = useGetAllEmployeesForList({
    filter: responsiblePersonFilter,
  });

  const isNotPermissionToEditCompany =
    isEditOwnPermission && assignTo !== ProcessTaskAssignEnum.PERSONS;

  const isAllowToEdit = !isTaskOwner && isEditOwnPermission;

  const isCompanyFieldDisabled = (disabled?: boolean) => {
    if (isNotPermissionToEditCompany) {
      return true;
    }

    return !!disabled;
  };

  const handleSelect = (field: string) => {
    resetTaskFormFields(field, form);
  };

  const assignToOptions = Object.values(ProcessTaskAssignEnum);

  const onFinish = ({ dueDays, dueTo, ...doc }: any) =>
    onSubmit({ ...(dueTo && { dueTo: dueTo.toDate() }), ...doc });

  return (
    <Form layout="vertical" initialValues={initialValue} form={form} onFinish={onFinish}>
      <Form.Item
        name="title"
        label={t('processBuilder:tasks.labels.title')}
        rules={[{ required: true, message: t('processBuilder:tasks.validation.taskNameRequired') }]}
      >
        <TextInput
          disabled={isAllowToEdit || isProcessesTask}
          placeholder={t('processBuilder:tasks.labels.buildTheHouse')}
          maxLength={50}
        />
      </Form.Item>

      <Form.Item
        name="details"
        label={t('processBuilder:tasks.labels.details')}
        rules={[{ required: true, message: t('processBuilder:tasks.validation.detailsRequired') }]}
      >
        <TextAreaInput
          showCount
          disabled={isAllowToEdit || isProcessesTask}
          maxLength={500}
          placeholder={t('processBuilder:tasks.labels.writeSomeDetails')}
        />
      </Form.Item>

      <Checklist form={form} isAllowToEdit={isAllowToEdit || isProcessesTask} />

      <Form.Item
        name="assignTo"
        label={t('processBuilder:tasks.labels.assignTo')}
        rules={[{ required: true, message: t('processBuilder:tasks.validation.assigneeRequired') }]}
      >
        <SelectInput
          onSelect={value => {
            setAssignTo(value);
            handleSelect('assignTo');
          }}
          disabled={isNotPermissionToEditCompany || isAllowToEdit || isProcessesTask}
          placeholder={t('processBuilder:tasks.assignToOptions.persons')}
        >
          {assignToOptions.map(value => (
            <SelectInput.Option
              key={value}
              value={value}
              disabled={isEditOwnPermission && value !== ProcessTaskAssignEnum.PERSONS}
            >
              {t(`processBuilder:tasks.assignToOptions.${value.toLowerCase()}`)}
            </SelectInput.Option>
          ))}
        </SelectInput>
      </Form.Item>

      {(assignTo === ProcessTaskAssignEnum.JOB ||
        assignTo === ProcessTaskAssignEnum.DEPARTMENT) && (
        <Fragment>
          <Form.Item
            name="companyId"
            label={t('common:labels.company')}
            rules={[{ required: true, message: t('jobs:validation.companyRequired') }]}
          >
            <SelectInput
              placeholder="Asteric.Co"
              disabled={isEditOwnPermission || isProcessesTask}
              onSelect={() => {
                handleSelect('departmentId');
              }}
            >
              {companies.map(({ _id, name }: any) => (
                <SelectInput.Option key={_id} value={_id}>
                  {name}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const companyId = getFieldValue('companyId');

              const company = companies.find((item: any) => item._id === companyId);

              return (
                <Form.Item
                  name="departmentId"
                  label={t('common:labels.department')}
                  rules={[
                    { required: true, message: t('common:validation.error.departmentRequired') },
                  ]}
                >
                  <SelectInput
                    disabled={isCompanyFieldDisabled(!companyId) || isProcessesTask}
                    placeholder="Mauris netus"
                    onSelect={id => {
                      setDepartmentId(id);
                      handleSelect('jobId');
                    }}
                  >
                    {(company ? company.departments : []).map(({ _id, name }: any) => (
                      <SelectInput.Option key={_id} value={_id}>
                        {name}
                      </SelectInput.Option>
                    ))}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>
        </Fragment>
      )}

      {assignTo === ProcessTaskAssignEnum.JOB && (
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => (
            <Form.Item
              name="jobId"
              label={t('common:labels.jobTitle')}
              rules={[{ required: true, message: t('common:validation.error.jobTitleRequired') }]}
            >
              <SelectInput
                placeholder={t('settings:permissionGroupSettings.placeholders.builders')}
                disabled={isCompanyFieldDisabled(!getFieldValue('departmentId')) || isProcessesTask}
              >
                {jobs.map(({ job }: any) => (
                  <SelectInput.Option key={job._id} value={job._id}>
                    {job.title}
                  </SelectInput.Option>
                ))}
              </SelectInput>
            </Form.Item>
          )}
        </Form.Item>
      )}

      {assignTo === ProcessTaskAssignEnum.PERSONS && (
        <Form.Item
          name="responsiblePersonsIds"
          label={t('processBuilder:tasks.labels.responsiblePersons')}
          rules={[
            { required: true, message: t('vacancies:validation.responsiblePersonsRequired') },
          ]}
        >
          <CheckboxSelect
            placeholder="Select from the list"
            disabled={isAllowToEdit || isProcessesTask}
            optionData={employees.map((item: any) => ({
              label: item.profile.displayName,
              value: item._id,
              disabled: item._id !== member?._id && isEditOwnPermission,
            }))}
          />
        </Form.Item>
      )}

      {isProcessTaskTemplate && (
        <Form.Item
          name="deadlineInDays"
          label={t('processBuilder:tasks.labels.expiresAt')}
          rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
        >
          <NumberInput
            suffix={` ${t('processBuilder:tasks.labels.days')}`}
            maxLength={10}
            placeholder={`3 ${t('processBuilder:tasks.labels.days')}`}
            thousandSeparator={false}
          />
        </Form.Item>
      )}

      {!isProcessTaskTemplate && (
        <Fragment>
          <Form.Item
            name="dueTo"
            label={t('processBuilder:tasks.labels.expiresAt')}
            rules={[{ required: true, message: t('common:validation.error.fieldRequired') }]}
          >
            <DatePickerInput
              disabled={isAllowToEdit || isProcessesTask}
              disabledDate={current => {
                const today = moment().startOf('day');

                return current && current.valueOf() < today.valueOf();
              }}
              lang={i18n.language}
              format="DD.MM.YYYY"
            />
          </Form.Item>
          <Form.Item
            name="status"
            label={t('common:labels.status')}
            rules={[
              { required: true, message: t('processBuilder:tasks.validation.statusRequired') },
            ]}
            initialValue={ProcessTaskStatusesEnum.OPEN}
          >
            <SelectInput
              disabled={!initialValue}
              placeholder={t('processBuilder:tasks.statuses.open')}
            >
              {[ProcessTaskStatusesEnum.OPEN, ProcessTaskStatusesEnum.COMPLETED].map(status => (
                <SelectInput.Option key={status} value={status}>
                  {t(`processBuilder:tasks.statuses.${status.toLowerCase()}`)}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>
          <Form.Item
            name="description"
            label={t('processBuilder:tasks.labels.description')}
          >
            <TextAreaInput
              showCount
              maxLength={500}
              placeholder={t('processBuilder:tasks.labels.writeSomeDetails')}
            />
          </Form.Item>
        </Fragment>
      )}

      <ButtonsWrapper marginTop={16}>
        <Button type="primary" htmlType="submit">
          {t('common:buttons.save')}
        </Button>
        <Button onClick={onCancel}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </Form>
  );
};

export default TaskEditForm;
